import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, QrCode, Wallet2, ReceiptText, Plus, LogOut, Image as ImageIcon, Megaphone, Users, Star, Trash2, Gift, UserPlus, Bell, Share2, Copy, Download, Printer } from "lucide-react";
import { api, apiErr, money, imgSrc } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Logo, TierBadge, Loading, ImageUploader, StarRating, LegalAgreeCheckbox, PublicFooter } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TrustBadge } from "@/components/ReferralFeedback";
import LocationPinMap from "@/components/LocationPinMap";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "leads", label: "Leads", icon: UserPlus },
  { id: "ask", label: "Ask for referrals", icon: Share2 },
  { id: "confirm", label: "Confirm visit", icon: QrCode },
  { id: "profile", label: "Profile", icon: ImageIcon },
  { id: "reward", label: "Reward", icon: Gift },
  { id: "updates", label: "Updates", icon: Megaphone },
  { id: "referrers", label: "Referrers", icon: Users },
  { id: "ratings", label: "Ratings", icon: Star },
  { id: "balance", label: "Balance", icon: Wallet2 },
  { id: "payouts", label: "Payouts", icon: ReceiptText },
  { id: "alerts", label: "Alerts", icon: Bell },
];

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  return `${days}d ago`;
}

const STATUS_PILL = {
  new: "bg-brand-gold/20 text-brand-gold-dark",
  contacted: "bg-blue-500/15 text-blue-700",
  converted: "bg-brand-green/15 text-brand-green",
  closed: "bg-secondary text-muted-foreground",
};

const RegisterBusiness = ({ onDone }) => {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ company_name: "", category_id: "", address: "", city: "", state: "", postal_code: "", phone: "" });
  const [branch, setBranch] = useState(null);
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [deliveryPending, setDeliveryPending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [agreeLegal, setAgreeLegal] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const applyClaimResponse = (resData, { resend = false } = {}) => {
    const mockCode = resData.dev_verification_code || "";
    setDevCode(mockCode);
    setDeliveryPending(resData.delivery === "pending");
    setSentTo(resData.sent_to || "");
    if (mockCode) {
      toast.info(`Verification code (mock): ${mockCode}`);
    } else if (resData.delivery === "pending") {
      toast.message(
        resData.sent_to
          ? `Sending code to ${resData.sent_to}…`
          : "Sending verification code…",
      );
    } else if (resend) {
      toast.success(
        resData.sent_to
          ? `A new code was sent to ${resData.sent_to}`
          : "A new verification code was sent",
      );
    } else {
      toast.success(
        resData.sent_to
          ? `Verification code sent to ${resData.sent_to}`
          : "Verification code sent",
      );
    }
  };

  const create = async () => {
    if (!agreeLegal) {
      toast.error("Please agree to the Terms and Privacy Policy before claiming your profile.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/branches", form);
      const res = await api.post(`/branches/${data.id}/claim`);
      setBranch(data);
      applyClaimResponse(res.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!branch) return;
    setLoading(true);
    try {
      const res = await api.post(`/branches/${branch.id}/claim`);
      applyClaimResponse(res.data, { resend: true });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    try {
      await api.post(`/branches/${branch.id}/verify`, { code });
      toast.success("Business verified!");
      onDone();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-8">
      <h1 className="text-2xl font-black tracking-tight">Register your business</h1>
      <p className="mt-1 text-muted-foreground">Create your branch and verify ownership to enable rewards.</p>
      {!branch ? (
        <div className="mt-6 space-y-3">
          <Input placeholder="Business name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="reg-name" />
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
            <SelectTrigger data-testid="reg-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Street address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="reg-address" />
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="reg-city" />
            <Input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="reg-state" />
            <Input placeholder="ZIP" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="reg-zip" />
          </div>
          <Input placeholder="Public phone (+1…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reg-phone" />
          <div className="rounded-[10px] border border-border bg-secondary/40 p-3">
            <p className="mb-2 text-xs text-muted-foreground">
              Review the Terms and Privacy Policy before claiming this profile. Ownership verification sends an SMS to the public business phone.
            </p>
            <LegalAgreeCheckbox checked={agreeLegal} onCheckedChange={setAgreeLegal} id="biz-legal-agree" />
          </div>
          <Button
            className="w-full bg-brand-green hover:bg-brand-green-dark"
            disabled={loading || !form.company_name || !form.category_id || !agreeLegal}
            onClick={create}
            data-testid="reg-create-btn"
          >
            {loading ? "Sending…" : "Create & send verification code"}
          </Button>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {devCode && <div className="rounded-[10px] bg-brand-gold/15 px-3 py-2 text-sm text-brand-gold-dark">Mock code: <b>{devCode}</b></div>}
          {deliveryPending && !devCode ? (
            <div className="rounded-[10px] bg-brand-green/10 px-3 py-2 text-sm text-brand-green-dark" data-testid="reg-delivery-pending">
              Sending code{sentTo ? ` to ${sentTo}` : ""}… if it doesn&apos;t arrive, tap Resend.
            </div>
          ) : null}
          <Input placeholder="Verification code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="reg-verify-code" />
          <Button className="w-full bg-brand-green hover:bg-brand-green-dark" disabled={loading} onClick={verify} data-testid="reg-verify-btn">
            {loading ? "Verifying…" : "Verify ownership"}
          </Button>
          <Button variant="outline" className="w-full" disabled={loading} onClick={resend} data-testid="reg-resend-btn">
            {loading ? "Sending…" : "Resend code"}
          </Button>
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ branch }) => {
  const [payouts, setPayouts] = useState([]);
  useEffect(() => { api.get("/payouts").then(({ data }) => setPayouts(data.filter((p) => p.branch_id === branch.id))); }, [branch.id]);
  const escrow = payouts.filter((p) => p.status === "escrow").reduce((s, p) => s + p.amount_cents, 0);
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <div className="rounded-[14px] border border-border bg-card p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Generosity</p>
        <div className="mt-2 flex items-center gap-3"><TierBadge tier={branch.payer_tier} /><span className="text-2xl font-black">{branch.generosity_score}</span></div>
      </div>
      <div className="rounded-[14px] border border-border bg-card p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Pending in escrow</p>
        <p className="mt-2 text-2xl font-black text-brand-gold-dark dark:text-brand-gold" data-testid="dash-escrow">{money(escrow)}</p>
      </div>
      <div className="rounded-[14px] border border-border bg-card p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Total payouts</p>
        <p className="mt-2 text-2xl font-black">{payouts.length}</p>
      </div>
    </div>
  );
};

const ConfirmVisit = () => {
  const [code, setCode] = useState("");
  const redeem = async () => {
    try {
      const { data } = await api.post(`/redemptions/${code.trim()}/redeem`);
      toast.success(data.message);
      setCode("");
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="max-w-md">
      <h2 className="text-lg font-bold">Confirm an in-store visit</h2>
      <p className="text-sm text-muted-foreground">Enter the customer's redemption code to confirm the visit and start the payout.</p>
      <div className="mt-4 flex gap-2">
        <Input placeholder="Redemption code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="confirm-code-input" />
        <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={redeem} data-testid="confirm-redeem-btn">Confirm</Button>
      </div>
    </div>
  );
};

const RewardSettings = ({ branch, reload }) => {
  const cfg = branch.reward_config || branch.reward || {};
  const [f, setF] = useState({
    reward_type: cfg.reward_type || "cash",
    reward_amount_cents: cfg.reward_amount_cents || 1000,
    reward_discount_pct: cfg.reward_discount_pct || 10,
  });
  const [pausing, setPausing] = useState(false);
  const reasons = branch.pause_reasons || [];
  const manuallyPaused = reasons.includes("manual");
  const balancePaused = reasons.includes("balance");
  const stateText = !reasons.length
    ? "Active"
    : manuallyPaused && balancePaused
      ? "Paused by you · also balance too low"
      : manuallyPaused
        ? "Paused by you"
        : "Paused — balance too low";

  const save = async () => {
    try {
      await api.patch(`/branches/${branch.id}`, {
        reward_type: f.reward_type,
        reward_amount_cents: f.reward_type === "cash" ? Number(f.reward_amount_cents) : null,
        reward_discount_pct: f.reward_type === "discount" ? Number(f.reward_discount_pct) : null,
      });
      toast.success("Reward saved");
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const togglePause = async () => {
    const next = !manuallyPaused;
    const ok = window.confirm(
      next
        ? "Pause your reward? Referrers will see that rewards are paused and won't earn until you unpause (and balance is funded)."
        : "Unpause your reward? It will go live again only if prepaid balance can cover the next payout."
    );
    if (!ok) return;
    setPausing(true);
    try {
      await api.post(`/branches/${branch.id}/reward/pause`, { paused: next });
      toast.success(next ? "Reward paused" : "Manual pause cleared");
      reload();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPausing(false); }
  };

  return (
    <div className="max-w-lg space-y-6">
      <div className="rounded-[14px] border border-border bg-card p-4" data-testid="reward-pause-panel">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Offer status</p>
        <p className="mt-1 text-lg font-semibold" data-testid="reward-pause-state">{stateText}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {manuallyPaused
            ? "Referrers see a paused notice. Topping up alone will not restore a manual pause."
            : balancePaused
              ? "Your prepaid balance can't cover the next payout. Top up to clear the balance pause."
              : "Referrers can earn this reward on conversions."}
        </p>
        <Button
          className="mt-3"
          variant={manuallyPaused ? "default" : "outline"}
          disabled={pausing}
          onClick={togglePause}
          data-testid="reward-pause-toggle"
        >
          {pausing ? "Saving…" : manuallyPaused ? "Unpause (clear my pause)" : "Pause reward"}
        </Button>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Reward type</label>
        <Select value={f.reward_type} onValueChange={(v) => setF({ ...f, reward_type: v })}>
          <SelectTrigger className="mt-1" data-testid="reward-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="discount">Discount %</SelectItem>
            <SelectItem value="thank_you">Thank you</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {f.reward_type === "cash" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Cash reward (cents)</label>
          <Input type="number" value={f.reward_amount_cents} onChange={(e) => setF({ ...f, reward_amount_cents: e.target.value })} data-testid="reward-amount-input" />
        </div>
      )}
      {f.reward_type === "discount" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Discount %</label>
          <Input type="number" value={f.reward_discount_pct} onChange={(e) => setF({ ...f, reward_discount_pct: e.target.value })} data-testid="reward-pct-input" />
        </div>
      )}
      <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={save} data-testid="save-reward-btn">Save reward</Button>
    </div>
  );
};

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const ProfileTab = ({ branch, reload }) => {
  const [f, setF] = useState({
    name: branch.company_name || "",
    category_id: branch.category_id || "",
    subcategory_id: branch.subcategory_id || "",
    phone: branch.phone || "",
    logo_url: branch.logo_url || "",
    portfolio_image_urls: branch.portfolio_image_urls || [],
    about: branch.about || "",
    website_url: branch.website_url || "",
    address: branch.address || "",
    city: branch.city || "",
    state: branch.state || "",
    postal_code: branch.postal_code || "",
    service_modes: branch.service_modes || ["in_store"],
    hours: branch.hours && Object.keys(branch.hours).length ? branch.hours : {},
    latitude: branch.latitude ?? null,
    longitude: branch.longitude ?? null,
    geocode_source: branch.geocode_source || "none",
  });
  const [cats, setCats] = useState([]);
  const [relocating, setRelocating] = useState(false);
  const [phoneStep, setPhoneStep] = useState("idle"); // idle | code
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [keywordsText, setKeywordsText] = useState("");
  const [approvedKeywords, setApprovedKeywords] = useState(branch.keywords || []);
  const [pendingKeywords, setPendingKeywords] = useState([]);
  const [keywordBusy, setKeywordBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/branches/${branch.id}/keywords`)
      .then(({ data }) => {
        setApprovedKeywords(data.approved || []);
        setPendingKeywords(data.pending || []);
      })
      .catch(() => {
        setApprovedKeywords(branch.keywords || []);
      });
  }, [branch.id, branch.keywords]);

  const subs = (cats.find((c) => c.id === f.category_id)?.subcategories) || [];
  const toggleMode = (m) => setF({ ...f, service_modes: f.service_modes.includes(m) ? f.service_modes.filter((x) => x !== m) : [...f.service_modes, m] });

  const save = async () => {
    try {
      const payload = {
        name: f.name,
        category_id: f.category_id || null,
        subcategory_id: f.subcategory_id || "",
        logo_url: f.logo_url || null,
        portfolio_image_urls: f.portfolio_image_urls,
        about: f.about,
        website_url: f.website_url,
        address: f.address,
        city: f.city || null,
        state: f.state || null,
        postal_code: f.postal_code || null,
        service_modes: f.service_modes,
        hours: f.hours,
      };
      if (f.geocode_source === "manual" && f.latitude != null && f.longitude != null) {
        payload.latitude = f.latitude;
        payload.longitude = f.longitude;
        payload.geocode_source = "manual";
      }
      const { data } = await api.patch(`/branches/${branch.id}`, payload);
      setF((s) => ({
        ...s,
        name: data.company_name || s.name,
        category_id: data.category_id || "",
        subcategory_id: data.subcategory_id || "",
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
        geocode_source: data.geocode_source || "none",
        address: data.address || s.address,
        city: data.city || "",
        state: data.state || "",
        postal_code: data.postal_code || "",
      }));
      toast.success("Profile saved");
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const requestPhoneOtp = async () => {
    setPhoneBusy(true);
    try {
      const { data } = await api.post(`/branches/${branch.id}/phone/request-otp`, { phone: f.phone });
      setPhoneStep("code");
      setDevCode(data.dev_verification_code || null);
      toast.success(`Code sent to ${data.sent_to}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPhoneBusy(false); }
  };

  const verifyPhoneOtp = async () => {
    setPhoneBusy(true);
    try {
      const { data } = await api.post(`/branches/${branch.id}/phone/verify`, { code: phoneCode });
      setPhoneStep("idle");
      setPhoneCode("");
      setDevCode(null);
      setF((s) => ({ ...s, phone: data.phone || s.phone }));
      toast.success("Phone updated");
      reload();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPhoneBusy(false); }
  };

  const onPinMove = async ({ lat, lng }) => {
    setF((s) => ({ ...s, latitude: lat, longitude: lng, geocode_source: "manual" }));
    try {
      const { data } = await api.patch(`/branches/${branch.id}`, {
        latitude: lat,
        longitude: lng,
        geocode_source: "manual",
      });
      setF((s) => ({
        ...s,
        latitude: data.latitude,
        longitude: data.longitude,
        geocode_source: data.geocode_source || "manual",
      }));
      toast.success("Map pin saved");
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const relocate = async () => {
    setRelocating(true);
    try {
      const { data } = await api.post(`/branches/${branch.id}/regeocode`);
      setF((s) => ({
        ...s,
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
        geocode_source: data.geocode_source || "geocoded",
        address: data.address || s.address,
      }));
      toast.success("Re-located from address");
      reload();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRelocating(false); }
  };
  return (
    <div className="max-w-2xl space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Business name</label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name" />
          <p className="mt-1 text-xs text-muted-foreground">You can rename once every 30 days. Changes are reviewed afterward.</p>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Category</label>
          <Select value={f.category_id || undefined} onValueChange={(v) => setF({ ...f, category_id: v, subcategory_id: "" })}>
            <SelectTrigger className="mt-1" data-testid="profile-category"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Subcategory</label>
          <Select value={f.subcategory_id || undefined} onValueChange={(v) => setF({ ...f, subcategory_id: v })} disabled={!f.category_id}>
            <SelectTrigger className="mt-1" data-testid="profile-subcategory"><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>
              {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-[14px] border border-border bg-card p-4 space-y-3" data-testid="profile-phone-panel">
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Business phone</label>
        <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone" disabled={phoneStep === "code"} />
        {phoneStep === "idle" ? (
          <Button type="button" variant="outline" disabled={phoneBusy || !f.phone || f.phone === branch.phone} onClick={requestPhoneOtp} data-testid="profile-phone-request-otp">
            {phoneBusy ? "Sending…" : "Verify new number"}
          </Button>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Code sent to the new number</label>
              <Input value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} data-testid="profile-phone-otp" placeholder="6-digit code" />
              {devCode ? <p className="mt-1 text-xs text-muted-foreground">Dev code: {devCode}</p> : null}
            </div>
            <Button type="button" disabled={phoneBusy || !phoneCode} onClick={verifyPhoneOtp} data-testid="profile-phone-verify">Confirm</Button>
            <Button type="button" variant="ghost" onClick={() => { setPhoneStep("idle"); setPhoneCode(""); }}>Cancel</Button>
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Logo</label>
        <div className="mt-2 flex items-center gap-4">
          {f.logo_url ? <img src={imgSrc(f.logo_url)} alt="logo" className="h-16 w-16 rounded-[10px] object-cover" data-testid="logo-preview" /> : <div className="h-16 w-16 rounded-[10px] bg-secondary" />}
          <ImageUploader label="Upload logo" testid="upload-logo" onUploaded={(url) => setF({ ...f, logo_url: url })} />
        </div>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Portfolio images</label>
        <div className="mt-2 flex flex-wrap gap-3">
          {f.portfolio_image_urls.map((u, i) => (
            <div key={i} className="relative">
              <img src={imgSrc(u)} alt={`portfolio-${i}`} className="h-24 w-24 rounded-[10px] object-cover" data-testid={`portfolio-img-${i}`} />
              <button className="absolute -right-2 -top-2 rounded-full bg-destructive p-1 text-white" onClick={() => setF({ ...f, portfolio_image_urls: f.portfolio_image_urls.filter((_, j) => j !== i) })} data-testid={`remove-portfolio-${i}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          <ImageUploader label="Add photo" testid="upload-portfolio" onUploaded={(url) => setF({ ...f, portfolio_image_urls: [...f.portfolio_image_urls, url] })} />
        </div>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">About</label>
        <Textarea value={f.about} onChange={(e) => setF({ ...f, about: e.target.value })} data-testid="profile-about" rows={3} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Website</label>
          <Input value={f.website_url} onChange={(e) => setF({ ...f, website_url: e.target.value })} data-testid="profile-website" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Street address</label>
          <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="profile-address" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">City</label>
          <Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="profile-city" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">State</label>
          <Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="profile-state" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">ZIP</label>
          <Input value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value })} data-testid="profile-zip" />
        </div>
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Map location</label>
          <Button type="button" variant="outline" size="sm" disabled={relocating || !(f.address || f.city || f.postal_code)} onClick={relocate} data-testid="relocate-from-address-btn">
            {relocating ? "Locating…" : "Re-locate from address"}
          </Button>
        </div>
        <p className="mb-2 text-xs text-muted-foreground">
          Drag the pin to set your exact spot. A manual pin survives address edits until you re-locate.
          {f.geocode_source === "manual" ? " (manual pin)" : f.geocode_source === "geocoded" ? " (from address)" : " (not set)"}
        </p>
        <LocationPinMap
          latitude={f.latitude}
          longitude={f.longitude}
          onChange={onPinMove}
          testId="owner-location-pin-map"
        />
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Service modes</label>
        <div className="mt-2 flex flex-wrap gap-4">
          {["in_store", "delivery", "remote"].map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm" data-testid={`mode-${m}`}>
              <Checkbox checked={f.service_modes.includes(m)} onCheckedChange={() => toggleMode(m)} /> {m.replace("_", " ")}
            </label>
          ))}
        </div>
      </div>

      <div data-testid="profile-keywords-panel">
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Keywords</label>
        <p className="mt-1 text-xs text-muted-foreground">
          Search terms shown as chips on browse cards. New keywords go to admin for approval.
        </p>
        {approvedKeywords.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {approvedKeywords.map((k) => (
              <span key={k} className="rounded-full border border-border bg-secondary/60 px-2.5 py-0.5 text-xs font-medium" data-testid={`keyword-approved-${k}`}>
                {k}
              </span>
            ))}
          </div>
        ) : null}
        {pendingKeywords.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {pendingKeywords.map((p) => (
              <span key={p.id} className="rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs text-muted-foreground" data-testid={`keyword-pending-${p.id}`}>
                {p.keyword} (pending)
              </span>
            ))}
          </div>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-2">
          <Input
            className="flex-1"
            placeholder="e.g. kitchen remodel, cabinets"
            value={keywordsText}
            onChange={(e) => setKeywordsText(e.target.value)}
            data-testid="profile-keywords-input"
          />
          <Button
            type="button"
            variant="outline"
            disabled={keywordBusy || !keywordsText.trim()}
            data-testid="profile-keywords-submit"
            onClick={async () => {
              setKeywordBusy(true);
              try {
                const { data } = await api.post(`/branches/${branch.id}/keywords`, { keyword: keywordsText });
                setKeywordsText("");
                const count = data.count ?? (data.submitted?.length || 0);
                if (count) toast.success(`${count} keyword${count === 1 ? "" : "s"} submitted for approval`);
                else toast.message("Nothing new to submit (already approved or pending)");
                const { data: refreshed } = await api.get(`/branches/${branch.id}/keywords`);
                setApprovedKeywords(refreshed.approved || []);
                setPendingKeywords(refreshed.pending || []);
              } catch (e) { toast.error(apiErr(e)); }
              finally { setKeywordBusy(false); }
            }}
          >
            {keywordBusy ? "Submitting…" : "Submit"}
          </Button>
        </div>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Hours</label>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {DAYS.map((d) => (
            <div key={d} className="flex items-center gap-2">
              <span className="w-10 text-sm capitalize text-muted-foreground">{d}</span>
              <Input className="h-9" placeholder="9-6 or closed" value={f.hours[d] || ""} onChange={(e) => setF({ ...f, hours: { ...f.hours, [d]: e.target.value } })} data-testid={`hours-${d}`} />
            </div>
          ))}
        </div>
      </div>

      <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={save} data-testid="save-profile-btn">Save profile</Button>
    </div>
  );
};

const UpdatesTab = ({ branch }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", image_url: "" });
  const load = () => api.get(`/branches/${branch.id}/updates`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [branch.id]);
  const post = async () => {
    if (!form.title || !form.body) return;
    try {
      await api.post(`/branches/${branch.id}/updates`, { ...form, image_url: form.image_url || null });
      setForm({ title: "", body: "", image_url: "" });
      toast.success("Update posted");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/branches/${branch.id}/updates/${id}`); load(); };
  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-[14px] border border-border bg-card p-5 space-y-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Post an update (shown publicly on your profile)</p>
        <Input placeholder="Title (e.g. New summer hours)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="update-title" />
        <Textarea placeholder="What's new?" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="update-body" rows={3} />
        <div className="flex items-center gap-3">
          {form.image_url && <img src={imgSrc(form.image_url)} alt="update" className="h-14 w-14 rounded-[8px] object-cover" />}
          <ImageUploader label="Attach photo" testid="upload-update" onUploaded={(url) => setForm({ ...form, image_url: url })} />
          <Button className="ml-auto bg-brand-green hover:bg-brand-green-dark" onClick={post} data-testid="post-update-btn">Post update</Button>
        </div>
      </div>
      <div className="space-y-3">
        {items.length === 0 ? <p className="text-muted-foreground">No updates yet.</p> : items.map((u) => (
          <div key={u.id} className="rounded-[12px] border border-border bg-card p-4" data-testid={`update-${u.id}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-semibold">{u.title}</h3>
              <button onClick={() => del(u.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-update-${u.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{u.body}</p>
            {u.image_url && <img src={imgSrc(u.image_url)} alt={u.title} className="mt-3 max-h-56 rounded-[10px] object-cover" />}
          </div>
        ))}
      </div>
    </div>
  );
};

const ReferrersTab = ({ branch }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/branches/${branch.id}/attribution`).then(({ data }) => setRows(data)); }, [branch.id]);
  if (rows === null) return <Loading />;
  return (
    <div>
      <h2 className="text-lg font-bold">Who referred what</h2>
      {rows.length === 0 ? <p className="mt-2 text-muted-foreground">No referrers yet.</p> : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4">Referrer</th><th className="py-2 pr-4">Trust</th><th className="py-2 pr-4">Referrals</th><th className="py-2 pr-4">Clicks</th><th className="py-2 pr-4">Conversions</th><th className="py-2 pr-4">Paid</th><th className="py-2">Pending</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.referrer_user_id} className="border-b border-border/50" data-testid={`attribution-${r.referrer_user_id}`}>
                  <td className="py-2 pr-4 font-medium">{r.referrer_name}</td>
                  <td className="py-2 pr-4"><TrustBadge badge={r.trust_badge} /></td>
                  <td className="py-2 pr-4">{r.referrals}</td>
                  <td className="py-2 pr-4">{r.clicks}</td>
                  <td className="py-2 pr-4">{r.conversions}</td>
                  <td className="py-2 pr-4 font-semibold text-brand-green">{money(r.paid_cents)}</td>
                  <td className="py-2 font-semibold text-brand-gold-dark dark:text-brand-gold">{money(r.pending_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RatingsTab = ({ branch }) => {
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [replies, setReplies] = useState({});
  const load = () => {
    api.get(`/branches/${branch.id}/ratings`).then(({ data }) => setData(data));
    api.get(`/branches/${branch.id}/quality-feedback`).then(({ data }) => setFeedback(data)).catch(() => setFeedback({ count: 0, items: [] }));
  };
  useEffect(() => { load(); }, [branch.id]);
  const respond = async (id) => {
    if (!replies[id]) return;
    try { await api.post(`/ratings/${id}/respond`, { response: replies[id] }); toast.success("Response saved"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (data === null) return <Loading />;
  return (
    <div className="max-w-2xl space-y-8">
      <div className="rounded-[14px] border border-border bg-card p-5" data-testid="quality-feedback-section">
        <h2 className="text-lg font-bold">Feedback</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          These notes come from people who were referred to you. They&apos;re anonymized.
          Repeated feedback may affect how often you show up in search — a heads-up so you can improve, not a penalty notice.
        </p>
        {!feedback || feedback.count === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="quality-feedback-empty">No experience feedback yet.</p>
        ) : feedback.detail_level === "summary" ? (
          <p className="mt-3 font-medium" data-testid="quality-feedback-summary">
            {feedback.message || "You have feedback to review"}
            <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs">{feedback.count}</span>
          </p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="quality-feedback-list">
            {feedback.items.map((item) => (
              <li key={item.id} className="rounded-[10px] border border-border bg-background px-3 py-2 text-sm" data-testid={`quality-flag-${item.id}`}>
                <span className="text-muted-foreground">{(item.created_at || "").slice(0, 10)}</span>
                <span className="mx-2 text-muted-foreground">·</span>
                {item.reason_label}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-4 flex items-center gap-3">
          <h2 className="text-lg font-bold">Ratings</h2>
          {data.average != null && <span className="flex items-center gap-1 font-semibold text-brand-gold-dark dark:text-brand-gold"><Star className="h-4 w-4 fill-brand-gold text-brand-gold" />{data.average} ({data.count})</span>}
        </div>
        {data.ratings.length === 0 ? <p className="text-muted-foreground">No ratings yet.</p> : (
          <div className="space-y-3">
            {data.ratings.map((r) => (
              <div key={r.id} className="rounded-[12px] border border-border bg-card p-4" data-testid={`rating-${r.id}`}>
                <div className="flex items-center gap-2">
                  <StarRating value={r.rating} size={18} readOnly />
                  {!r.revealed && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">awaiting reveal</span>}
                </div>
                {r.owner_response ? (
                  <p className="mt-2 rounded-[8px] bg-secondary p-2 text-sm"><span className="font-semibold text-brand-green">Your response:</span> {r.owner_response}</p>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <Input placeholder="Respond to this rating…" value={replies[r.id] || ""} onChange={(e) => setReplies({ ...replies, [r.id]: e.target.value })} data-testid={`respond-input-${r.id}`} />
                    <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => respond(r.id)} data-testid={`respond-btn-${r.id}`}>Reply</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Balance = ({ branch }) => {
  const [bal, setBal] = useState(null);
  const load = () => api.get(`/branches/${branch.id}/balance`).then(({ data }) => setBal(data));
  useEffect(() => { load(); }, [branch.id]);
  const topUp = async (amt) => {
    try { await api.post(`/branches/${branch.id}/balance/top-up`, { amount_cents: amt }); toast.success("Balance topped up"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!bal) return <Loading />;
  return (
    <div className="max-w-lg">
      <div className="rounded-[14px] bg-gradient-to-br from-brand-green via-brand-green-dark to-[#0e3f30] p-6 text-white">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold">Prepaid balance</p>
        <p className="mt-1 text-4xl font-black" data-testid="branch-balance">{money(bal.balance_cents)}</p>
        <p className="mt-1 text-sm text-white/80">Rewards auto-pause if the balance can't cover the next payout.</p>
      </div>
      <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Top up</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {bal.replenish_amounts_cents.map((a) => (
          <Button key={a} variant="outline" onClick={() => topUp(a)} data-testid={`topup-${a}`}>{money(a)}</Button>
        ))}
      </div>
    </div>
  );
};

const Payouts = ({ branch }) => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/payouts").then(({ data }) => setItems(data.filter((p) => p.branch_id === branch.id)));
  useEffect(() => { load(); }, [branch.id]);
  const dispute = async (p) => {
    const reason = window.prompt("Reason for dispute?");
    if (!reason) return;
    try { await api.post(`/conversions/${p.conversion_id}/dispute`, { reason }); toast.success("Dispute opened"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <h2 className="text-lg font-bold">Payouts</h2>
      <div className="mt-3 flex flex-col gap-2">
        {items.length === 0 ? <p className="text-muted-foreground">No payouts yet.</p> : items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-4 py-3" data-testid={`payout-${p.id}`}>
            <div className="flex-1"><p className="font-semibold">{money(p.amount_cents)} <span className="text-xs text-muted-foreground">to {p.referrer_name}</span></p><p className="text-xs text-muted-foreground">fee {money(p.platform_fee_cents)}</p></div>
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-bold capitalize">{p.status}</span>
            {p.status === "escrow" && <Button size="sm" variant="outline" onClick={() => dispute(p)} data-testid={`dispute-${p.id}`}>Dispute</Button>}
          </div>
        ))}
      </div>
    </div>
  );
};

const LeadsTab = ({ branch, onNewCount }) => {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState(null);
  const [notes, setNotes] = useState({});
  const load = () => {
    const params = filter !== "all" ? { status: filter } : {};
    api.get(`/branches/${branch.id}/leads`, { params }).then(({ data }) => {
      setRows(data.leads || []);
      onNewCount?.(data.new_count ?? 0);
    }).catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { load(); }, [branch.id, filter]);
  const patch = async (id, body) => {
    try {
      await api.patch(`/leads/${id}`, body);
      toast.success("Lead updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (rows === null) return <Loading />;
  return (
    <div className="max-w-3xl" data-testid="leads-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">Leads</h2>
          <p className="text-sm text-muted-foreground">People who said they&apos;ll reach out after a referral.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="leads-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-[14px] border border-dashed border-border bg-card/50 p-8 text-center" data-testid="leads-empty">
          <UserPlus className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-semibold">No leads yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            When someone you were referred taps &quot;Thanks — I&apos;ll reach out,&quot; they show up here with their contact info so you can follow up.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((lead) => (
            <div key={lead.id} className="rounded-[12px] border border-border bg-card p-4" data-testid={`lead-${lead.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{lead.referred_name}</p>
                  {lead.referred_phone ? (
                    <a href={`tel:${lead.referred_phone}`} className="text-sm font-medium text-brand-green underline-offset-2 hover:underline" data-testid={`lead-phone-${lead.id}`}>
                      {lead.referred_phone}
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground">No phone on file</p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>Referred by {lead.referrer_name}</span>
                    <TrustBadge badge={lead.trust_badge} />
                    <span>· {timeAgo(lead.created_at)}</span>
                  </div>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_PILL[lead.status] || STATUS_PILL.closed}`} data-testid={`lead-status-${lead.id}`}>
                  {lead.status}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {lead.status === "new" && (
                  <Button size="sm" variant="outline" onClick={() => patch(lead.id, { status: "contacted" })} data-testid={`lead-contacted-${lead.id}`}>Mark contacted</Button>
                )}
                {(lead.status === "new" || lead.status === "contacted") && (
                  <>
                    <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => patch(lead.id, { status: "converted" })} data-testid={`lead-converted-${lead.id}`}>Mark converted</Button>
                    <Button size="sm" variant="ghost" onClick={() => patch(lead.id, { status: "closed" })} data-testid={`lead-closed-${lead.id}`}>Close</Button>
                  </>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <Input
                  placeholder="Add a note…"
                  value={notes[lead.id] ?? lead.owner_note ?? ""}
                  onChange={(e) => setNotes({ ...notes, [lead.id]: e.target.value })}
                  data-testid={`lead-note-${lead.id}`}
                />
                <Button size="sm" variant="outline" onClick={() => patch(lead.id, { owner_note: notes[lead.id] ?? lead.owner_note ?? "" })} data-testid={`lead-note-save-${lead.id}`}>
                  Save note
                </Button>
              </div>
              {lead.owner_note ? <p className="mt-2 text-sm text-muted-foreground">Note: {lead.owner_note}</p> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AlertsTab = ({ user, refresh }) => {
  const leads = user?.notification_prefs?.leads || { push: true, sms: true, email: true };
  const [prefs, setPrefs] = useState({
    push: leads.push !== false,
    sms: leads.sms !== false,
    email: leads.email !== false,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me", { notification_prefs: { leads: prefs } });
      toast.success("Alert preferences saved");
      refresh?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-lg space-y-4" data-testid="alerts-tab">
      <div>
        <h2 className="text-lg font-bold">Lead alerts</h2>
        <p className="text-sm text-muted-foreground">Choose how you&apos;re notified when someone says they&apos;ll reach out.</p>
      </div>
      {[
        { key: "push", label: "Push / in-app" },
        { key: "sms", label: "SMS to my phone (recommended for hot leads)" },
        { key: "email", label: "Email to my inbox" },
      ].map((row) => (
        <label key={row.key} className="flex items-center gap-3 text-sm" data-testid={`alert-pref-${row.key}`}>
          <Checkbox checked={!!prefs[row.key]} onCheckedChange={(v) => setPrefs({ ...prefs, [row.key]: !!v })} />
          {row.label}
        </label>
      ))}
      <Button className="bg-brand-green hover:bg-brand-green-dark" disabled={saving} onClick={save} data-testid="save-alert-prefs">
        {saving ? "Saving…" : "Save preferences"}
      </Button>
    </div>
  );
};

const ASK_STATUS_PILL = {
  open: "bg-secondary text-muted-foreground",
  sent: "bg-blue-500/15 text-blue-700",
  joined: "bg-brand-green/15 text-brand-green",
  first_referral: "bg-brand-gold/20 text-brand-gold-dark",
  expired: "bg-secondary text-muted-foreground",
};

const ASK_STATUS_LABEL = {
  open: "Open",
  sent: "Sent",
  joined: "Joined",
  first_referral: "First referral made",
  expired: "Expired",
};

function absoluteAskUrl(askPath) {
  const origin = window.location.origin;
  return `${origin}${askPath.startsWith("/") ? askPath : `/${askPath}`}`;
}

function qrImageUrl(url, size = 280) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}`;
}

async function downloadQrPng(url, filename) {
  const res = await fetch(qrImageUrl(url, 512));
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function printAskCard({ businessName, askUrl, rewardLabel }) {
  const w = window.open("", "_blank", "noopener,noreferrer,width=480,height=640");
  if (!w) {
    toast.error("Allow pop-ups to print the card");
    return;
  }
  const qr = qrImageUrl(askUrl, 220);
  const safeName = String(businessName || "us").replace(/[<>&]/g, "");
  w.document.write(`<!doctype html><html><head><title>Refer ${safeName}</title>
<style>
  body{font-family:Georgia,serif;margin:0;padding:40px;text-align:center;color:#111;background:#fff}
  .kicker{font-size:11px;letter-spacing:.18em;text-transform:uppercase;color:#1a6b4a;font-weight:700;margin-bottom:12px}
  h1{font-size:24px;margin:0 0 8px}
  p{margin:0 0 18px;color:#444;font-size:15px;line-height:1.4}
  img{width:220px;height:220px}
  .biz{margin-top:18px;font-size:16px;font-weight:700}
  .url{margin-top:12px;font-size:11px;word-break:break-all;color:#666}
</style></head><body>
  <div class="kicker">ReferLinc</div>
  <h1>We're on ReferLinc</h1>
  <p>Refer us, earn rewards · Earn ${rewardLabel}</p>
  <img src="${qr}" alt="QR code" />
  <div class="biz">${safeName}</div>
  <div class="url">${askUrl}</div>
  <script>window.onload=()=>{window.print();}</script>
</body></html>`);
  w.document.close();
}

async function downloadAskCardPdf({ businessName, askUrl, rewardLabel, filename }) {
  const { jsPDF } = await import("jspdf");
  const qrRes = await fetch(qrImageUrl(askUrl, 400));
  const qrBlob = await qrRes.blob();
  const qrDataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(qrBlob);
  });
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  let y = 72;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(26, 107, 74);
  doc.text("REFERLINC", pageW / 2, y, { align: "center" });
  y += 28;
  doc.setTextColor(17, 17, 17);
  doc.setFontSize(22);
  doc.text("We're on ReferLinc", pageW / 2, y, { align: "center" });
  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(68, 68, 68);
  doc.text("Refer us, earn rewards", pageW / 2, y, { align: "center" });
  y += 18;
  doc.text(`Earn ${rewardLabel}`, pageW / 2, y, { align: "center" });
  y += 24;
  const qrSize = 180;
  doc.addImage(qrDataUrl, "PNG", (pageW - qrSize) / 2, y, qrSize, qrSize);
  y += qrSize + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(17, 17, 17);
  doc.text(String(businessName || "Our business"), pageW / 2, y, { align: "center" });
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(102, 102, 102);
  doc.text(askUrl, pageW / 2, y, { align: "center", maxWidth: pageW - 96 });
  doc.save(filename);
}

const AskTab = ({ branch }) => {
  const [data, setData] = useState(null);
  const [phonesText, setPhonesText] = useState("");
  const [boostDollars, setBoostDollars] = useState("");
  const [expiresDays, setExpiresDays] = useState("");
  const [sendSms, setSendSms] = useState(true);
  const [sending, setSending] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedPhones, setSelectedPhones] = useState(() => new Set());
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = () => {
    api.get(`/branches/${branch.id}/ask`).then(({ data: d }) => {
      setData(d);
      if (d.default_expiry_days && !expiresDays) setExpiresDays(String(d.default_expiry_days));
    }).catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { load(); }, [branch.id]);

  useEffect(() => {
    api.get("/me/contacts?role=person")
      .then(({ data: d }) => {
        const list = (d.contacts || [])
          .map((c) => ({
            key: c.phone_hash || c.contact_key,
            name: c.person?.name || c.label || "Contact",
            phone: c.person?.phone || null,
          }))
          .filter((c) => c.phone);
        setContacts(list);
      })
      .catch(() => setContacts([]));
  }, []);

  if (data === null) return <Loading />;

  const permanentUrl = absoluteAskUrl(data.permanent.ask_url);
  const badgeHtml = `<a href="${permanentUrl}?src=web" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:10px 14px;border-radius:8px;background:#1a6b4a;color:#fff;font-family:system-ui,sans-serif;font-weight:600;text-decoration:none">Earn by referring us on ReferLinc</a>`;
  const boostCents = Math.round(Number(boostDollars || 0) * 100) || 0;
  const earnPreview = data.standard_reward_cents + boostCents;

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Could not copy");
    }
  };

  const toggleContact = (phone) => {
    setSelectedPhones((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  };

  const createInvites = async ({ shareLinkOnly = false } = {}) => {
    const typed = phonesText.split(/[\n,;]+/).map((p) => p.trim()).filter(Boolean);
    const phones = shareLinkOnly
      ? []
      : [...new Set([...typed, ...selectedPhones])];
    if (!shareLinkOnly && phones.length === 0) {
      toast.error("Add at least one phone number or pick a contact, or create a shareable link");
      return;
    }
    setSending(true);
    try {
      const { data: res } = await api.post(`/branches/${branch.id}/ask/invites`, {
        phones,
        boost_bonus_cents: boostCents,
        expires_days: boostCents > 0 ? Number(expiresDays) || data.default_expiry_days : null,
        send_sms: shareLinkOnly ? false : sendSms,
      });
      const created = res.invites || [];
      toast.success(shareLinkOnly ? "Shareable ask link ready" : `Created ${created.length} invite(s)`);
      if (shareLinkOnly && created[0]?.ask_url) {
        await copy(absoluteAskUrl(created[0].ask_url), "Ask link copied");
      }
      setPhonesText("");
      setSelectedPhones(new Set());
      setBoostDollars("");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSending(false);
    }
  };

  const onDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadAskCardPdf({
        businessName: data.company_name || "us",
        askUrl: `${permanentUrl}?src=qr`,
        rewardLabel: money(data.standard_reward_cents),
        filename: `referlinc-ask-card-${branch.id}.pdf`,
      });
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not build PDF — try Print card instead");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="ask-tab">
      <div>
        <h2 className="text-lg font-bold">Ask for referrals</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Invite people to refer {data.company_name}. Standard reward is {money(data.standard_reward_cents)}.
          Balance {money(data.balance_cents)}. Daily invite cap {data.daily_cap}.
        </p>
      </div>

      <div className="rounded-[14px] border border-border bg-card p-5 space-y-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Compose invite</p>
        {contacts.length > 0 ? (
          <div className="space-y-2" data-testid="ask-contacts-picker">
            <p className="text-xs font-semibold text-muted-foreground">Synced contacts</p>
            <div className="max-h-36 space-y-1 overflow-y-auto rounded-[10px] border border-border p-2">
              {contacts.map((c) => (
                <label key={c.key || c.phone} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-secondary/60">
                  <Checkbox
                    checked={selectedPhones.has(c.phone)}
                    onCheckedChange={() => toggleContact(c.phone)}
                  />
                  <span className="font-medium">{c.name}</span>
                  <span className="text-muted-foreground">{c.phone}</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
        <Textarea
          placeholder={"Phones, one per line or comma-separated\n+15551234567"}
          value={phonesText}
          onChange={(e) => setPhonesText(e.target.value)}
          rows={3}
          data-testid="ask-phones"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">One-time boost ($)</label>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="0 = standard only"
              value={boostDollars}
              onChange={(e) => setBoostDollars(e.target.value)}
              data-testid="ask-boost-dollars"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              First conversion earns {money(earnPreview)}
              {boostCents > 0 ? ` (need ≥ ${money(earnPreview)} balance)` : ""}
            </p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Boost expires (days)</label>
            <Input
              type="number"
              min="1"
              value={expiresDays}
              onChange={(e) => setExpiresDays(e.target.value)}
              disabled={!boostCents}
              data-testid="ask-expires-days"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={sendSms} onCheckedChange={(v) => setSendSms(!!v)} data-testid="ask-send-sms" />
          Send SMS when phones are provided
        </label>
        <div className="flex flex-wrap gap-2">
          <Button
            className="bg-brand-green hover:bg-brand-green-dark"
            disabled={sending}
            onClick={() => createInvites({ shareLinkOnly: false })}
            data-testid="ask-send-invites"
          >
            {sending ? "Sending…" : "Send invites"}
          </Button>
          <Button
            variant="outline"
            disabled={sending}
            onClick={() => createInvites({ shareLinkOnly: true })}
            data-testid="ask-create-link"
          >
            Create shareable link
          </Button>
        </div>
      </div>

      <div className="rounded-[14px] border border-border bg-card p-5 space-y-4" data-testid="ask-promote-kit">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Promote kit</p>
        <p className="text-sm text-muted-foreground">
          Permanent ask link (standard reward only). Scans and joins are tracked as QR or web in invite status.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input readOnly value={permanentUrl} className="flex-1 min-w-[200px]" data-testid="ask-permanent-url" />
          <Button variant="outline" size="icon" onClick={() => copy(permanentUrl, "Link copied")} data-testid="ask-copy-permanent">
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap items-start gap-4">
          <img
            src={qrImageUrl(`${permanentUrl}?src=qr`)}
            alt="Ask QR code"
            width={140}
            height={140}
            className="rounded-[8px] border border-border"
            data-testid="ask-qr-img"
          />
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={() => downloadQrPng(`${permanentUrl}?src=qr`, `referlinc-ask-${branch.id}.png`)}
              data-testid="ask-qr-download"
            >
              <Download className="mr-2 h-4 w-4" /> Download QR PNG
            </Button>
            <Button
              variant="outline"
              disabled={pdfBusy}
              onClick={onDownloadPdf}
              data-testid="ask-pdf-download"
            >
              <Download className="mr-2 h-4 w-4" /> {pdfBusy ? "Building PDF…" : "Download PDF card"}
            </Button>
            <Button
              variant="outline"
              onClick={() => printAskCard({
                businessName: data.company_name || "us",
                askUrl: `${permanentUrl}?src=qr`,
                rewardLabel: money(data.standard_reward_cents),
              })}
              data-testid="ask-print-card"
            >
              <Printer className="mr-2 h-4 w-4" /> Print card
            </Button>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">HTML badge</label>
          <Textarea readOnly value={badgeHtml} rows={3} className="mt-1 font-mono text-xs" data-testid="ask-badge-html" />
          <Button className="mt-2" variant="outline" size="sm" onClick={() => copy(badgeHtml, "Badge HTML copied")} data-testid="ask-copy-badge">
            Copy HTML
          </Button>
        </div>
      </div>

      <div>
        <h3 className="font-semibold">Invite status</h3>
        {data.invites.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No invites yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Phone / source</th>
                  <th className="py-2 pr-3">Boost</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2">Link</th>
                </tr>
              </thead>
              <tbody>
                {data.invites.map((inv) => (
                  <tr key={inv.id} className="border-b border-border/50" data-testid={`ask-invite-${inv.id}`}>
                    <td className="py-2 pr-3 text-muted-foreground">{timeAgo(inv.created_at)}</td>
                    <td className="py-2 pr-3">{inv.phone || inv.source || "—"}</td>
                    <td className="py-2 pr-3">{inv.boost_bonus_cents ? money(inv.boost_bonus_cents) : "—"}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ASK_STATUS_PILL[inv.status] || ASK_STATUS_PILL.open}`}>
                        {ASK_STATUS_LABEL[inv.status] || inv.status}
                      </span>
                    </td>
                    <td className="py-2">
                      <button
                        type="button"
                        className="text-brand-green hover:underline"
                        onClick={() => copy(absoluteAskUrl(inv.ask_url), "Invite link copied")}
                      >
                        Copy
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const VALID_TABS = new Set(TABS.map((t) => t.id));

export default function BusinessPortal() {
  const { user, ready, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [branches, setBranches] = useState(null);
  const [selected, setSelected] = useState(null);
  const [newLeadCount, setNewLeadCount] = useState(0);
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return t && VALID_TABS.has(t) ? t : "dashboard";
  });

  const load = async () => {
    const { data } = await api.get("/my/branches");
    setBranches(data);
    // Honour a ?branch=<id> deep link when the user actually owns that branch.
    const wanted = searchParams.get("branch");
    if (!selected) {
      if (wanted && data.some((b) => b.id === wanted)) setSelected(wanted);
      else if (data.length) setSelected(data[0].id);
    }
  };
  useEffect(() => { if (ready && !user) nav(`/login?redirect=${encodeURIComponent("/business" + window.location.search)}`); else if (user) load(); }, [ready, user]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/branches/${selected}/leads`).then(({ data }) => setNewLeadCount(data.new_count ?? 0)).catch(() => {});
  }, [selected]);

  // Keep the URL in sync so the portal stays deep-linkable / shareable.
  const selectTab = (t) => {
    setTab(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  const selectBranch = (id) => {
    setSelected(id);
    const next = new URLSearchParams(searchParams);
    next.set("branch", id);
    setSearchParams(next, { replace: true });
  };

  if (!ready || !user || branches === null) return <Loading />;
  if (branches.length === 0) return (
    <div className="flex min-h-screen flex-col">
      <div className="border-b border-border bg-background/80 px-4 py-3 backdrop-blur-xl"><Logo /></div>
      <div className="flex-1">
        <RegisterBusiness onDone={load} />
      </div>
      <PublicFooter />
    </div>
  );

  const branch = branches.find((b) => b.id === selected) || branches[0];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 px-4 py-3 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            {branches.length > 1 && (
              <Select value={selected} onValueChange={selectBranch}>
                <SelectTrigger className="w-48" data-testid="branch-selector"><SelectValue /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.company_name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button variant="ghost" size="icon" onClick={() => { logout(); nav("/"); }} data-testid="business-logout"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-4 py-6">
        <div className="mb-6 flex items-center justify-between">
          <div><h1 className="text-2xl font-black tracking-tight">{branch.company_name}</h1><p className="text-muted-foreground">{branch.label}</p></div>
        </div>
        <div className="mb-6 flex flex-wrap gap-2 border-b border-border">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => selectTab(t.id)} data-testid={`biz-tab-${t.id}`}
              className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${tab === t.id ? "border-brand-green text-brand-green" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
              {t.id === "leads" && newLeadCount > 0 ? (
                <span className="rounded-full bg-brand-gold px-1.5 py-0.5 text-[10px] font-bold text-brand-ink" data-testid="leads-new-badge">{newLeadCount}</span>
              ) : null}
            </button>
          ))}
        </div>
        {tab === "dashboard" && <Dashboard branch={branch} />}
        {tab === "leads" && <LeadsTab branch={branch} onNewCount={setNewLeadCount} />}
        {tab === "ask" && <AskTab branch={branch} />}
        {tab === "confirm" && <ConfirmVisit />}
        {tab === "profile" && <ProfileTab branch={branch} reload={load} />}
        {tab === "reward" && <RewardSettings branch={branch} reload={load} />}
        {tab === "updates" && <UpdatesTab branch={branch} />}
        {tab === "referrers" && <ReferrersTab branch={branch} />}
        {tab === "ratings" && <RatingsTab branch={branch} />}
        {tab === "balance" && <Balance branch={branch} />}
        {tab === "payouts" && <Payouts branch={branch} />}
        {tab === "alerts" && <AlertsTab user={user} refresh={refresh} />}
      </div>
    </div>
  );
}
